import React from "react";
import Reveal from "../components/site/Reveal";
import WaitlistForm from "../components/site/WaitlistForm";
import { CONTACT, BOOKING_URL, GBP_URL } from "../lib/site";
import { Phone, Mail, MapPin, ArrowUpRight, Star } from "lucide-react";
import { useSeo, dentalClinicSchema } from "../lib/seo";

export default function Contact() {
  useSeo({
    title: "Contact Alae Dental. Woodbridge Dentist | Book Online",
    description:
      "Get in touch with Alae Dental in Vaughan / Woodbridge. Book online, request a callback, or reach out with any questions about our care.",
    path: "/contact",
    jsonLd: dentalClinicSchema(),
    jsonLdId: "contact-jsonld",
  });

  return (
    <main className="bg-linen text-umber">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 bg-paper paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Contact</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-umber max-w-4xl">
              We'd love to <span className="font-display-italic">hear from you.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
              Have a question before we open, or want priority scheduling when
              we do? Reach out. We'd be glad to help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Details + Map */}
      <section className="py-20 md:py-28 paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <div>
                <div className="flex items-center gap-3 font-meta text-forest mb-3">
                  <MapPin size={14} /> Visit
                </div>
                <p className="font-display text-2xl text-umber leading-snug">
                  {CONTACT.addressLines[0]}<br />
                  {CONTACT.addressLines[1]}
                </p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div>
                <div className="flex items-center gap-3 font-meta text-forest mb-3">
                  <Phone size={14} /> Phone
                </div>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  className="font-display text-2xl text-umber leading-snug hover:text-forest transition-colors"
                >
                  {CONTACT.phoneLabel}
                </a>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div>
                <div className="flex items-center gap-3 font-meta text-forest mb-3">
                  <Mail size={14} /> Email
                </div>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="font-display text-2xl text-umber leading-snug hover:text-forest transition-colors"
                >
                  {CONTACT.email}
                </a>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div>
                <div className="font-meta text-forest mb-3">Hours</div>
                <ul className="space-y-2 text-umber/85">
                  {CONTACT.hours.map((h) => (
                    <li key={h.days} className="flex justify-between gap-6 max-w-xs">
                      <span className="font-display text-lg">{h.days}</span>
                      <span className="text-umber/70 text-sm pt-1">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              {/* PLACEHOLDER: replace with real location */}
              <div className="relative aspect-[4/3] overflow-hidden shadow-paper border border-umber/15">
                <iframe
                  title="Alae Dental. 9097 Jane St, Vaughan, ON"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full grayscale-[35%] contrast-95"
                  style={{ border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Book / Callback */}
      <section className="bg-paper paper-grain py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Book Your Visit</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-umber tracking-tight">
              Ready when <span className="font-display-italic">you are.</span>
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
              Book online in minutes, or leave your details below and we'll
              reach out within 24 hours to help you find the right time.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300 text-center inline-flex items-center justify-center gap-2"
              >
                Book Online <ArrowUpRight size={14} />
              </a>
              <a
                href={GBP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-umber/40 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-all duration-300 text-center inline-flex items-center justify-center gap-2"
              >
                <Star size={14} /> See us on Google
              </a>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-14 bg-linen/60 border border-umber/15 p-8 md:p-12">
              <p className="font-meta text-umber/60 mb-6">
                Or request a callback
              </p>
              <WaitlistForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Emergency */}
      <section className="bg-forest text-paper paper-grain py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-meta text-paper/60 mb-4">Emergency</div>
            <h2 className="font-display text-3xl md:text-4xl text-paper leading-tight">
              Dental Emergency?
            </h2>
            <p className="mt-4 text-paper/80 leading-relaxed max-w-md">
              If you're in pain right now, call us directly. We'll do everything
              we can to help right away.
            </p>
          </div>
          <div className="md:text-right">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="inline-flex items-center gap-3 border border-paper/40 rounded-full px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-paper hover:text-forest transition-all duration-300"
            >
              <Phone size={14} /> Call {CONTACT.phoneLabel}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
